import ProductCardExpanderLegacy from '@components/components/ProductComponentsLegacy/ProductCardExpander/ProductCardExpander';
import ProductCardHeaderLegacy from '@components/components/ProductComponentsLegacy/ProductCardHeader/ProductCardHeader';
import ProductCardOptions from '@components/components/ProductComponentsLegacy/ProductCardOptions/ProductCardOptions';
import ProductCardEyecatcher from '@components/components/ProductComponentsLegacy/ProductCardEyecatcher/ProductCardEyecatcher';
import ProductCardSpecials from '@components/components/ProductComponentsLegacy/ProductCardSpecials/ProductCardSpecials';
import ProductCardOkoOption from '@components/components/ProductComponentsLegacy/ProductCardOkoOption/ProductCardOkoOption';
import styles from '@components/components/ProductComponentsLegacy/ProductCard/ProductCard.module.scss';
import React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useDialogStore } from '@ariakit/react';
import { Button, Card, Link } from '@wienenergiegithub/ui-next';

const ProductCardLegacy = ({
  product,
  okoCTA,
  okoShow,
  registerButton,
  orderDialog,
  to,
  className,
  icon,
  price,
  hideDetailLink,
  children,
  options,
  productOption,
  handleDropdownChange,
  fixHeight,
  detailLink,
}) => {
  const {
    type,
    eyecatcher,
    background,
    title,
    recommended: recommendedProduct,
    product_color: productColor,
    is_tarifwelt_new: isTarifweltNew,
    is_tanke_kwh: isTankeweltNew,
    customer_type: customerType,
    oko_disclaimer: okoDisclaimer,
  } = product;

  const promoDialog = useDialogStore({ animated: true });
  // link with deep link to product detail page
  let linkToDetailPage = to;
  const params = [
    productOption?.key,
    productOption?.mixtype,
    productOption?.bindung,
    productOption?.capprice,
  ]
    .filter(item => !!item)
    .join('-');
  linkToDetailPage += `?options=${params}`;

  // link to product overview page when on frontpage
  const handleLinkClick = linkUrl => e => {
    e.preventDefault();
    window.location.href = linkUrl;
  };

  const ariaCardLabel = eyecatcher?.text || '';
  const noOkoAndPhone = !product.oko || !product.phone || !product.show_oko;

  // decide Customer Type and tarifSubline (under the  title)
  // will not be displayed at the same time. tarifSubline overrides customerSubline
  const tarifSubline = product.tarif_subline ? product.tarif_subline : '';
  let customerSubline = '';
  if (noOkoAndPhone && customerType === 'newcustomers') {
    customerSubline = 'Für Neukund*innen';
  }
  if (noOkoAndPhone && customerType === 'existingcustomers') {
    customerSubline = 'Für Bestandskund*innen';
  }

  const localRegisterButton =
    type === 'tanke' || type === 'business_tanke' ? (
      <Button
        className={styles.cta}
        isElevated
        render={
          <Link
            rel="noopener"
            target={product.content.cta?.target || '_blank'}
            to={product.content.cta?.url}
          />
        }
      >
        {product.content.cta?.title || 'BESTELLEN'}
      </Button>
    ) : (
      registerButton
    );

  return (
    <div
      className={classNames(
        styles.wrapper,
        {
          [styles.eyecatcher]: eyecatcher,
          [styles.recommended]: recommendedProduct,
        },
        styles[eyecatcher?.theme],
      )}
    >
      {(eyecatcher?.text || recommendedProduct) && (
        <ProductCardEyecatcher
          eyecatcher={eyecatcher}
          promoDialog={promoDialog}
          recommendedProduct={recommendedProduct}
        />
      )}
      <Card
        className={classNames(styles.container, className, styles[background])}
      >
        <ProductCardHeaderLegacy
          ariaCardLabel={ariaCardLabel}
          background={
            (isTarifweltNew === false || productColor === 'default') &&
            type !== 'tanke' &&
            type !== 'business_tanke'
              ? background
              : null
          }
          icon={icon}
          isTankeweltNeu={isTankeweltNew}
          price={price}
          productColor={productColor}
          tarifSubline={tarifSubline || customerSubline}
          tarifweltNeu={isTarifweltNew}
          title={title}
        />
        <div className={styles.body}>
          <div
            className={classNames(styles.options, {
              [styles.isOko]: okoCTA,
              [styles.fixHeight]: fixHeight,
            })}
          >
            {/* Tarifwelt neu has Options dropdown, other tarifs have Specials (with checkmarks) */}
            {isTarifweltNew && (
              <ProductCardOptions
                handleDropdownChange={handleDropdownChange}
                options={options}
                product={product}
                productOption={productOption}
                tarifweltNeu={isTarifweltNew}
              />
            )}
            {product.benefits && !isTarifweltNew && (
              <ProductCardSpecials
                fixHeight={fixHeight}
                items={product.benefits}
                multiplePrices={product.prices?.length > 1}
                okoDisclaimer={okoDisclaimer}
                okoShow={okoShow}
                title={title}
              />
            )}
            <ProductCardOkoOption
              handleLinkClick={handleLinkClick}
              orderDialog={orderDialog}
              product={product}
              registerButton={localRegisterButton}
            />
            <div>
              <ProductCardExpanderLegacy
                isDiscounted={product.is_discounted}
                items={product.content.list}
                productType={type}
              />
            </div>
          </div>
          <div className={styles.actions}>
            {!hideDetailLink && !detailLink && (
              <Button
                data-purpose="cta-detail"
                render={<Link to={linkToDetailPage} />}
                theme="primary"
                variant="link"
              >
                Details
              </Button>
            )}
            {detailLink && (
              <Button
                data-purpose="cta-detail"
                render={
                  type === 'tanke' ? (
                    <Link rel="noopener" target="_blank" to={detailLink.url} />
                  ) : (
                    <Link to={detailLink.url} />
                  )
                }
                theme="primary"
                variant="link"
              >
                {detailLink.title}
              </Button>
            )}
          </div>
        </div>
        {children}
      </Card>
    </div>
  );
};

ProductCardLegacy.propTypes = {
  /** Special Product action - e.g. ProductCardNonBuyable */
  children: PropTypes.node,
  className: PropTypes.string,
  /** ProductSpecific Icon */
  icon: PropTypes.node.isRequired,
  /** ProductCardPrice element - 1-n */
  price: PropTypes.node,
  /** URL to detail page */
  to: PropTypes.string.isRequired,
};

export default ProductCardLegacy;
